import React, { useEffect, useState } from "react";
import { Modal, Box, Flex, Text, Loader, createStyles } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { QR, BTN_RESET } from "../../pages/Home/QuarterlyReportsPage/theme";
import { generateReportPdf } from "./pdfExport";
import { captureDocumentCanvas, sliceCanvasToPages, CONTENT_HEIGHT_PT } from "./pdfCapture";
import { getReportForExport } from "../../services/quarterlyReport";

// The modal chrome only — mirrors the prototype's #pdf-modal exactly: a
// header bar (title + Download PDF + a plain "✕"), a gray scrollable viewer
// holding paper-white pages, and a footer with a right-aligned Close button
// (the prototype's own .pdf-foot has a base centered style, but its actual
// usage overrides that with an inline justify-content: flex-end) — not
// Mantine's own title/close-button chrome. The pages themselves are real
// captured images (see captureDocumentCanvas/sliceCanvasToPages) — the same
// capture the real PDF download is built from — rather than a DOM
// approximation, so the preview always shows exactly the pages the download
// will produce, with identical page breaks.
const useStyles = createStyles(() => ({
  bar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 20px",
    background: QR.surface,
    borderBottom: `1px solid ${QR.line}`,
  },
  barTitle: { fontSize: 16, fontWeight: 700, letterSpacing: "-0.01em", fontFamily: "Inter", color: QR.ink },
  barActions: { display: "flex", gap: 10, alignItems: "center" },
  downloadBtn: {
    ...BTN_RESET,
    height: 40,
    padding: "0 18px",
    borderRadius: 8,
    background: QR.accent,
    color: QR.ink,
    fontWeight: 700,
    fontSize: 13.5,
    fontFamily: "Inter",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    "&:hover": { background: QR.accentHover },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  closeX: {
    ...BTN_RESET,
    width: 34,
    height: 34,
    borderRadius: 8,
    color: QR.muted,
    fontSize: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": { background: QR.surfaceAlt, color: QR.ink },
  },
  viewer: {
    flex: 1,
    overflowY: "auto",
    background: "#E8E8EC",
    padding: 24,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 20,
  },
  page: {
    width: 640,
    flexShrink: 0,
    background: "#fff",
    boxShadow: "0 4px 18px rgba(0,0,0,.2)",
    borderRadius: 2,
    overflow: "hidden",
  },
  // Real vector text around the captured image, matching the real PDF's own
  // header run-line + "Page X of Y" footer (drawn separately from the
  // captured content there too) rather than being baked into the image.
  pageHeader: {
    padding: "14px 32px 8px",
    fontSize: 10,
    color: "#9a9a9a",
    borderBottom: "1px solid #eee",
    marginBottom: 4,
  },
  pageImage: { width: "100%", display: "block", lineHeight: 0 },
  pageFooter: {
    padding: "6px 0 12px",
    textAlign: "center",
    fontSize: 10,
    color: "#aaa",
  },
  foot: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 14,
    padding: "12px 20px",
    borderTop: `1px solid ${QR.line}`,
    background: QR.surface,
  },
  btnGhost: {
    ...BTN_RESET,
    height: 40,
    padding: "0 16px",
    borderRadius: 8,
    border: `1px solid ${QR.line}`,
    background: QR.surface,
    fontWeight: 600,
    fontSize: 13.5,
    color: QR.ink2,
    fontFamily: "Inter",
    "&:hover": { background: QR.surfaceSoft },
  },
}));

// Read-only, print-layout rendering of the current draft — matches the
// DBHDS Quarterly PCR (ISP v4.0) structure, broken into real pages the same
// way the downloaded PDF is (not one long scrolling document).
//
// Always re-fetches the canonical saved copy from the server when opened
// (rather than reusing whatever possibly-unsaved local edits are in memory)
// so the previewed/downloaded document always matches what's actually
// persisted — the version that would legally be on file — not an in-progress
// edit the user hasn't saved yet.
const PdfPreviewModal = ({ opened, onClose, draft: draftProp }) => {
  const { classes } = useStyles();
  const [downloading, setDownloading] = useState(false);
  const [freshDraft, setFreshDraft] = useState(null);
  const [loadingFresh, setLoadingFresh] = useState(false);
  const [pages, setPages] = useState([]);
  const [capturing, setCapturing] = useState(false);

  useEffect(() => {
    if (!opened) {
      setFreshDraft(null);
      setPages([]);
      return;
    }
    if (!draftProp?.id) return;
    setLoadingFresh(true);
    getReportForExport(draftProp.id).then((data) => {
      setFreshDraft(data || draftProp);
      setLoadingFresh(false);
    });
    // deliberately keyed on the id, not the whole object — draftProp is a new
    // reference on every local edit (keystrokes), which would refetch on
    // every keystroke instead of once when the modal opens for this report
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, draftProp?.id]);

  const draft = freshDraft || draftProp;

  // Captures and slices into pages once the real draft is in hand — the same
  // capture/slice pdfExport.js uses for the actual download, sliced at the
  // same content height (not the full page height) so the preview's page
  // breaks land in exactly the same place the download's will, once the
  // header/footer band drawn around each image below is accounted for.
  useEffect(() => {
    if (loadingFresh || !draft) return;
    let cancelled = false;
    setCapturing(true);
    setPages([]);
    (async () => {
      const canvas = await captureDocumentCanvas(draft);
      if (cancelled) return;
      setPages(sliceCanvasToPages(canvas, CONTENT_HEIGHT_PT));
      setCapturing(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, loadingFresh]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await generateReportPdf(draft);
    } finally {
      setDownloading(false);
      onClose();
    }
  };

  if (!draftProp) return null;

  const Header = (
    <Box className={classes.bar}>
      <Text className={classes.barTitle}>Report Preview</Text>
      <Flex className={classes.barActions}>
        <button className={classes.downloadBtn} disabled={downloading || loadingFresh} onClick={handleDownload}>
          {downloading ? <Loader size={13} color="currentColor" /> : <IconDownload size={15} stroke={2} />}
          {downloading ? "Preparing…" : "Download PDF"}
        </button>
        <button className={classes.closeX} onClick={onClose} aria-label="Close preview">
          ✕
        </button>
      </Flex>
    </Box>
  );

  const showLoader = loadingFresh || capturing || !pages.length;
  const runHead = draft
    ? `${draft.title || "Quarterly Person-Centered Review"} · ${draft.clientName || ""} · ${draft.periodLabel || ""}`
    : "";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={null}
      withCloseButton={false}
      padding={0}
      radius={16}
      size={800}
      zIndex={1100}
      styles={{
        body: { padding: 0, display: "flex", flexDirection: "column", height: "100%" },
        content: { display: "flex", flexDirection: "column", height: "min(88vh, 940px)" },
      }}
    >
      {Header}

      <Box className={classes.viewer}>
        {showLoader ? (
          <Flex justify="center" align="center" py={60}>
            <Loader color="violet" />
          </Flex>
        ) : (
          pages.map((page, i) => (
            <Box key={i} className={classes.page}>
              <Box className={classes.pageHeader}>{runHead}</Box>
              <img src={page.dataUrl} alt={`Page ${i + 1} of ${pages.length}`} className={classes.pageImage} />
              <Box className={classes.pageFooter}>
                Page {i + 1} of {pages.length}
              </Box>
            </Box>
          ))
        )}
      </Box>

      <Flex className={classes.foot}>
        <button className={classes.btnGhost} onClick={onClose}>
          Close
        </button>
      </Flex>
    </Modal>
  );
};

export default PdfPreviewModal;
